// Top-down auto-layout for flows. Computes node positions only; nodes, config and edges are
// left untouched.
//
// Method (a Sugiyama variant):
//  1. Rank = longest path from a root, so every edge points down.
//  2. Edges spanning several ranks get dummy "lanes" so their route stays free.
//  3. Order per rank: DFS in handle order (true left, false right, switch cases in handle
//     order), then barycenter + transpose while crossings decrease.
//  4. x: quadratic optimisation with minimum gaps per rank (blockwise, PAVA). Main-path edges
//     and lanes weigh heavily (straight), side branches lightly.
//  5. y: rows aligned at the top, row height = tallest node + gap.
// React Flow draws every edge as a single bezier curve (bottom -> top) without waypoints: a
// long edge only stays clear of other nodes when source and target share a column. That is
// why the longest main edge into a merge node gets priority.

export interface LayoutNode {
  id: string;
  type?: string;
  config?: Record<string, unknown>;
  width: number;
  height: number;
}

export interface LayoutEdge {
  source: string;
  target: string;
  sourceHandle?: string | null;
}

export type Positions = Record<string, { x: number; y: number }>;

export interface LayoutStats {
  crossings: number;
  edgeHits: number;
  overlaps: number;
  width: number;
}

const GAP_X = 56;   // node <-> node within a row
const GAP_XL = 36;  // node <-> lane
const GAP_LL = 20;  // lane <-> lane
const LANE_W = 12;
const GAP_Y = 64;   // between rows
const W_MAIN = 100, W_LANE = 100, W_SIDE = 1;

interface Seg { u: string; v: string; frac: number; w: number; off: number; e: number }
interface VNode { id: string; real: boolean; n?: LayoutNode; w: number; h: number; rank: number; ins: Seg[]; outs: Seg[] }
interface Box { x: number; y: number; w: number; h: number }

/** Position of an output handle as a fraction of the node width (see EvalNode / SwitchNode). */
function portFrac(n: LayoutNode | undefined, handle: string | null): number {
  if (!n) return 0.5;
  if (n.type === 'eval') return handle === 'true' ? 0.28 : handle === 'false' ? 0.72 : 0.5;
  if (n.type === 'switch') {
    const cases = Array.isArray(n.config?.cases) ? (n.config!.cases as { id?: string }[]) : [];
    const slots = cases.length + 1;
    if (handle === 'default') return slots / (slots + 1);
    const i = cases.findIndex(k => k.id === handle);
    return i >= 0 ? (i + 1) / (slots + 1) : 0.5;
  }
  return 0.5;
}

interface LayoutResult { positions: Positions; sinkCandidates: string[]; stats: LayoutStats }

// sink: node IDs that should sit as low as possible (directly above their earliest successor)
// instead of directly below their predecessor.
function layout(nodes: LayoutNode[], allEdges: LayoutEdge[], sinkDown: Set<string>): LayoutResult {
  const byId = new Map(nodes.map(n => [n.id, n]));
  const edgesIn = allEdges.filter(e => byId.has(e.source) && byId.has(e.target));
  const touched = new Set<string>();
  edgesIn.forEach(e => { touched.add(e.source); touched.add(e.target); });
  const loose = nodes.filter(n => !touched.has(n.id));

  const V = new Map<string, VNode>();
  for (const n of nodes) if (touched.has(n.id)) V.set(n.id, { id: n.id, real: true, n, w: n.width, h: n.height, rank: 0, ins: [], outs: [] });

  const positions: Positions = {};
  const boxes = new Map<string, Box>();

  // ---- 1. Ranks ----
  const outE = new Map<string, number[]>([...V.keys()].map(k => [k, []]));
  const deg = new Map<string, number>([...V.keys()].map(k => [k, 0]));
  edgesIn.forEach((e, i) => { outE.get(e.source)!.push(i); deg.set(e.target, deg.get(e.target)! + 1); });
  const roots = [...V.keys()].filter(k => deg.get(k) === 0);
  const rank = new Map<string, number>(roots.map(r => [r, 0]));
  const topo: string[] = [];
  const q = [...roots];
  while (q.length) {
    const u = q.shift()!;
    topo.push(u);
    for (const i of outE.get(u)!) {
      const t = edgesIn[i].target;
      rank.set(t, Math.max(rank.get(t) ?? 0, rank.get(u)! + 1));
      deg.set(t, deg.get(t)! - 1);
      if (deg.get(t) === 0) q.push(t);
    }
  }
  if (topo.length !== V.size) throw new Error('The flow contains a cycle and cannot be laid out.');
  for (const u of [...topo].reverse()) {
    if (!sinkDown.has(u) || !outE.get(u)!.length) continue;
    rank.set(u, Math.min(...outE.get(u)!.map(i => rank.get(edgesIn[i].target)!)) - 1);
  }
  for (const [k, v] of V) v.rank = rank.get(k)!;
  const maxRank = V.size ? Math.max(...rank.values()) : -1;

  // ---- Main path ----
  const edges = edgesIn.map((e, i) => ({
    i, u: e.source, v: e.target, h: e.sourceHandle ?? null,
    span: rank.get(e.target)! - rank.get(e.source)!,
    frac: portFrac(byId.get(e.source), e.sourceHandle ?? null),
  }));
  const height = new Map<string, number>(), reach = new Map<string, Set<string>>();
  for (const u of [...topo].reverse()) {
    let h = 0;
    const r = new Set([u]);
    for (const i of outE.get(u)!) {
      const t = edgesIn[i].target;
      h = Math.max(h, rank.get(t)! - rank.get(u)! + height.get(t)!);
      reach.get(t)!.forEach(x => r.add(x));
    }
    height.set(u, h);
    reach.set(u, r);
  }
  // An edge between adjacent rows cannot cover a node, a long one can: its curve leaves the
  // handle vertically and only swings towards the target halfway. Long edges therefore get
  // the straight column, short ones may run diagonally.
  // Main output: long edge before short, then the longest way down, then mass.
  const mainOut = new Map<string, number>();
  for (const u of V.keys()) {
    const cand = edges.filter(e => e.u === u);
    if (!cand.length) continue;
    cand.sort((a, b) =>
      Number(b.span > 1) - Number(a.span > 1) ||
      (b.span + height.get(b.v)!) - (a.span + height.get(a.v)!) ||
      reach.get(b.v)!.size - reach.get(a.v)!.size ||
      a.frac - b.frac || a.i - b.i);
    mainOut.set(u, cand[0].i);
  }
  // Main input: among the predecessors' main outputs the longest edge, then the chain that
  // starts highest.
  const mainIn = new Map<string, number>(), chainStart = new Map<string, number>();
  for (const v of topo) {
    const cand = edges.filter(e => e.v === v && mainOut.get(e.u) === e.i);
    if (cand.length) {
      cand.sort((a, b) => b.span - a.span || chainStart.get(a.u)! - chainStart.get(b.u)! || a.i - b.i);
      mainIn.set(v, cand[0].i);
      chainStart.set(v, chainStart.get(cand[0].u)!);
    } else chainStart.set(v, rank.get(v)!);
  }

  // ---- 2. Segments and lanes ----
  const addSeg = (a: string, b: string, p: Omit<Seg, 'u' | 'v'>) => {
    const s = { u: a, v: b, ...p };
    V.get(a)!.outs.push(s);
    V.get(b)!.ins.push(s);
  };
  for (const e of edges) {
    const isOut = mainOut.get(e.u) === e.i, isIn = mainIn.get(e.v) === e.i;
    const portOff = (e.frac - 0.5) * V.get(e.u)!.w;
    if (e.span === 1) {
      const main = isOut && isIn;
      addSeg(e.u, e.v, { frac: e.frac, w: main ? W_MAIN : W_SIDE, off: main ? 0 : portOff, e: e.i });
      continue;
    }
    let prev = e.u;
    for (let r = rank.get(e.u)! + 1; r < rank.get(e.v)!; r++) {
      const id = `~${e.i}~${r}`;
      V.set(id, { id, real: false, w: LANE_W, h: 0, rank: r, ins: [], outs: [] });
      addSeg(prev, id, prev === e.u
        ? { frac: e.frac, w: isOut ? W_MAIN : W_SIDE, off: isOut ? 0 : portOff, e: e.i }
        : { frac: 0.5, w: W_LANE, off: 0, e: e.i });
      prev = id;
    }
    addSeg(prev, e.v, { frac: 0.5, w: isIn ? W_MAIN : W_SIDE, off: 0, e: e.i });
  }

  // ---- 3. Order per rank ----
  let layers: string[][] = Array.from({ length: maxRank + 1 }, () => []);
  const seen = new Set<string>();
  const visit = (v: string) => {
    if (seen.has(v)) return;
    seen.add(v);
    layers[V.get(v)!.rank].push(v);
    for (const s of [...V.get(v)!.outs].sort((a, b) => a.frac - b.frac || a.e - b.e)) visit(s.v);
  };
  roots.forEach(visit);

  const pos = new Map<string, number>();
  const index = (i: number) => layers[i].forEach((v, j) => pos.set(v, j));
  layers.forEach((_, i) => index(i));
  const crossPair = (i: number) => {
    const es: [number, number][] = [];
    for (const u of layers[i]) for (const s of V.get(u)!.outs) es.push([pos.get(u)! + s.frac, pos.get(s.v)!]);
    let c = 0;
    for (let a = 0; a < es.length; a++) for (let b = a + 1; b < es.length; b++)
      if ((es[a][0] - es[b][0]) * (es[a][1] - es[b][1]) < 0) c++;
    return c;
  };
  const totalCross = () => { let c = 0; for (let i = 0; i < maxRank; i++) c += crossPair(i); return c; };
  const around = (i: number) => (i > 0 ? crossPair(i - 1) : 0) + (i < maxRank ? crossPair(i) : 0);
  const reorder = (i: number, key: (v: string) => number | null) => {
    const L = layers[i];
    const keys = L.map(key);
    const mov = L.map((v, j) => ({ v, k: keys[j], j }))
      .filter((o): o is { v: string; k: number; j: number } => o.k != null)
      .sort((a, b) => a.k - b.k || a.j - b.j);
    let m = 0;
    layers[i] = L.map((v, j) => (keys[j] == null ? v : mov[m++].v));
    index(i);
  };
  const down = () => {
    for (let i = 1; i <= maxRank; i++) reorder(i, v => {
      const s = V.get(v)!.ins;
      return s.length ? s.reduce((a, x) => a + pos.get(x.u)! + x.frac, 0) / s.length : null;
    });
  };
  const up = () => {
    for (let i = maxRank - 1; i >= 0; i--) reorder(i, v => {
      const s = V.get(v)!.outs;
      return s.length ? s.reduce((a, x) => a + pos.get(x.v)! + 1 - x.frac, 0) / s.length : null;
    });
  };
  const transpose = () => {
    for (let pass = 0; pass < 20; pass++) {
      let improved = false;
      for (let i = 0; i <= maxRank; i++) {
        const L = layers[i];
        for (let j = 0; j + 1 < L.length; j++) {
          const before = around(i);
          [L[j], L[j + 1]] = [L[j + 1], L[j]]; index(i);
          if (around(i) < before) improved = true;
          else { [L[j], L[j + 1]] = [L[j + 1], L[j]]; index(i); }
        }
      }
      if (!improved) break;
    }
  };
  const snapshot = () => layers.map(L => [...L]);
  transpose();
  let best = snapshot(), bestC = totalCross();
  for (let it = 0; it < 24; it++) {
    if (it % 2 === 0) down(); else up();
    transpose();
    const c = totalCross();
    if (c < bestC) { bestC = c; best = snapshot(); }
  }
  layers = best;
  layers.forEach((_, i) => index(i));

  // ---- 4. x coordinates ----
  const sep = (a: string, b: string) => {
    const A = V.get(a)!, B = V.get(b)!;
    const g = A.real && B.real ? GAP_X : (!A.real && !B.real ? GAP_LL : GAP_XL);
    return (A.w + B.w) / 2 + g;
  };
  // The solver runs thousands of sweeps, so everything that stays fixed during it (layer order,
  // offsets S, neighbour weights) is precomputed into index arrays.
  const vIds = [...V.keys()];
  const vIdx = new Map(vIds.map((id, k) => [id, k]));
  const xs = new Float64Array(vIds.length);
  const nbrStart = new Int32Array(vIds.length + 1);
  const nbr: number[] = [], nbrW: number[] = [], nbrOff: number[] = [];
  const sumW = new Float64Array(vIds.length);
  vIds.forEach((id, k) => {
    nbrStart[k] = nbr.length;
    let sw = 0;
    for (const s of V.get(id)!.ins) { nbr.push(vIdx.get(s.u)!); nbrW.push(s.w); nbrOff.push(s.off); sw += s.w; }
    for (const s of V.get(id)!.outs) { nbr.push(vIdx.get(s.v)!); nbrW.push(s.w); nbrOff.push(-s.off); sw += s.w; }
    sumW[k] = sw;
  });
  nbrStart[vIds.length] = nbr.length;
  const layerIdx = layers.map(L => Int32Array.from(L, v => vIdx.get(v)!));
  const layerS = layers.map(L => {
    const S = new Float64Array(L.length);
    for (let j = 1; j < L.length; j++) S[j] = S[j - 1] + sep(L[j - 1], L[j]);
    return S;
  });
  layerIdx.forEach((L, i) => L.forEach((k, j) => { xs[k] = layerS[i][j]; }));

  const maxLayer = Math.max(0, ...layers.map(L => L.length));
  const bW = new Float64Array(maxLayer), bM = new Float64Array(maxLayer), bC = new Int32Array(maxLayer);
  const solveLayer = (i: number) => {
    const L = layerIdx[i], S = layerS[i], n = L.length;
    let nb = 0;
    for (let j = 0; j < n; j++) {
      const k = L[j], sw = sumW[k];
      let d = xs[k];
      if (sw) {
        let swd = 0;
        for (let p = nbrStart[k]; p < nbrStart[k + 1]; p++) swd += nbrW[p] * (xs[nbr[p]] + nbrOff[p]);
        d = swd / sw;
      }
      bW[nb] = sw || 1e-9; bM[nb] = d - S[j]; bC[nb] = 1; nb++;
      while (nb > 1 && bM[nb - 2] > bM[nb - 1]) {
        const w = bW[nb - 2] + bW[nb - 1];
        bM[nb - 2] = (bM[nb - 2] * bW[nb - 2] + bM[nb - 1] * bW[nb - 1]) / w;
        bW[nb - 2] = w; bC[nb - 2] += bC[nb - 1]; nb--;
      }
    }
    let moved = 0, j = 0;
    for (let b = 0; b < nb; b++) for (let c = 0; c < bC[b]; c++, j++) {
      const nx = bM[b] + S[j];
      moved = Math.max(moved, Math.abs(nx - xs[L[j]]));
      xs[L[j]] = nx;
    }
    return moved;
  };
  for (let sweeps = 0; sweeps < 20000; sweeps++) {
    let moved = 0;
    for (let i = 0; i <= maxRank; i++) moved = Math.max(moved, solveLayer(i));
    for (let i = maxRank; i >= 0; i--) moved = Math.max(moved, solveLayer(i));
    if (moved < 0.005) break;
  }
  const x = new Map(vIds.map((id, k) => [id, xs[k]]));

  // ---- 5. y coordinates ----
  const rowH: number[] = Array(maxRank + 1).fill(0);
  for (const v of V.values()) if (v.real) rowH[v.rank] = Math.max(rowH[v.rank], v.h);
  const rowY: number[] = [];
  let y = 0;
  for (let r = 0; r <= maxRank; r++) { rowY.push(y); y += rowH[r] + GAP_Y; }

  let minX = Infinity, maxX = 0;
  for (const v of V.values()) if (v.real) minX = Math.min(minX, x.get(v.id)! - v.w / 2);
  for (const v of V.values()) if (v.real) {
    const px = Math.round(x.get(v.id)! - v.w / 2 - minX), py = rowY[v.rank];
    positions[v.id] = { x: px, y: py };
    boxes.set(v.id, { x: px, y: py, w: v.w, h: v.h });
    maxX = Math.max(maxX, px + v.w);
  }

  // Unconnected nodes: comments go above the root, side by side; other steps that are not
  // wired up yet go into a column to the right of the graph.
  const comments = loose.filter(n => n.type === 'comment');
  const others = loose.filter(n => n.type !== 'comment');
  if (comments.length) {
    const rootBox = roots.length ? boxes.get(roots[0])! : { x: 0, y: 0, w: 0, h: 0 };
    const total = comments.reduce((a, n) => a + n.width, 0) + GAP_X * (comments.length - 1);
    let cx = Math.round(rootBox.x + rootBox.w / 2 - total / 2);
    const top = Math.min(...comments.map(n => -(n.height + GAP_Y)));
    for (const n of comments) {
      positions[n.id] = { x: cx, y: top };
      boxes.set(n.id, { x: cx, y: top, w: n.width, h: n.height });
      cx += n.width + GAP_X;
    }
  }
  let oy = 0;
  const ox = V.size ? maxX + GAP_X * 2 : 0;
  for (const n of others) {
    positions[n.id] = { x: ox, y: oy };
    boxes.set(n.id, { x: ox, y: oy, w: n.width, h: n.height });
    oy += n.height + GAP_Y;
  }

  // Candidates for sinkDown: one input, every output skips at least one row.
  const sinkCandidates = [...V.values()]
    .filter(v => v.real && v.ins.length === 1 && v.outs.length && edges.filter(e => e.u === v.id).every(e => e.span >= 2))
    .map(v => v.id);

  return { positions, sinkCandidates, stats: { crossings: bestC, ...quality(edgesIn, boxes, byId) } };
}

// ---------- Quality: edges that (as drawn by the editor) run through other nodes ----------

// Same control points as React Flow's getBezierPath for a bottom -> top edge (curvature 0.25).
function bezierPoints(sx: number, sy: number, tx: number, ty: number): [number, number][] {
  const dy = ty - sy;
  const c = dy >= 0 ? 0.5 * dy : 0.25 * 25 * Math.sqrt(-dy);
  const P = [[sx, sy], [sx, sy + c], [tx, ty - c], [tx, ty]];
  const pts: [number, number][] = [];
  for (let k = 1; k < 50; k++) {
    const t = k / 50, m = 1 - t;
    const f = [m * m * m, 3 * m * m * t, 3 * m * t * t, t * t * t];
    pts.push([f.reduce((a, w, j) => a + w * P[j][0], 0), f.reduce((a, w, j) => a + w * P[j][1], 0)]);
  }
  return pts;
}

function quality(edges: LayoutEdge[], boxes: Map<string, Box>, byId: Map<string, LayoutNode>) {
  let edgeHits = 0;
  for (const e of edges) {
    const a = boxes.get(e.source)!, b = boxes.get(e.target)!;
    const pts = bezierPoints(a.x + portFrac(byId.get(e.source), e.sourceHandle ?? null) * a.w, a.y + a.h, b.x + b.w / 2, b.y);
    const hit = new Set<string>();
    for (const [px, py] of pts) for (const [id, bx] of boxes) {
      if (id === e.source || id === e.target) continue;
      if (px > bx.x - 2 && px < bx.x + bx.w + 2 && py > bx.y - 2 && py < bx.y + bx.h + 2) hit.add(id);
    }
    edgeHits += hit.size;
  }
  let overlaps = 0;
  const arr = [...boxes.values()];
  for (let i = 0; i < arr.length; i++) for (let j = i + 1; j < arr.length; j++) {
    const A = arr[i], B = arr[j];
    if (A.x < B.x + B.w && B.x < A.x + A.w && A.y < B.y + B.h && B.y < A.y + A.h) overlaps++;
  }
  // The graph starts at x = 0; comments above the root may stick out to the left and do not count.
  const width = arr.length ? Math.round(Math.max(...arr.map(b => b.x + b.w))) : 0;
  return { edgeHits, overlaps, width };
}

/**
 * Lays the flow out top-down and returns a position for every node. Greedily pulls nodes down
 * (see `sinkDown` in `layout`) while edges through foreign nodes, crossings or width decrease.
 * Throws if the edges contain a cycle.
 */
export function autoLayout(nodes: LayoutNode[], edges: LayoutEdge[]): { positions: Positions; stats: LayoutStats } {
  const score = (s: LayoutStats) => s.edgeHits * 1000 + s.crossings * 10 + s.width / 100;
  let sink = new Set<string>();
  let res = layout(nodes, edges, sink);
  for (let improved = true; improved;) {
    improved = false;
    for (const c of res.sinkCandidates) {
      if (sink.has(c)) continue;
      const trial = new Set([...sink, c]);
      const r2 = layout(nodes, edges, trial);
      if (score(r2.stats) < score(res.stats)) { sink = trial; res = r2; improved = true; }
    }
  }
  return { positions: res.positions, stats: res.stats };
}
